import React, { useState, useEffect, useCallback } from 'react';

const CHECK_TYPES = ['uptime', 'sentry', 'github', 'security', 'backup'];

const CHECK_LABELS = {
  uptime: 'Uptime',
  sentry: 'Errors',
  github: 'GitHub',
  security: 'Security',
  backup: 'Backups',
};

const CHECK_ICONS = {
  uptime: 'fa-heartbeat',
  sentry: 'fa-bug',
  github: 'fa-code-branch',
  security: 'fa-shield-alt',
  backup: 'fa-database',
};

const STATUS_COLORS = {
  green: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50', border: 'border-green-200' },
  yellow: { bg: 'bg-yellow-400', text: 'text-yellow-700', light: 'bg-yellow-50', border: 'border-yellow-200' },
  red: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-50', border: 'border-red-200' },
  unknown: { bg: 'bg-gray-300', text: 'text-gray-500', light: 'bg-gray-50', border: 'border-gray-200' },
};

function StatusMatrix() {
  const [matrix, setMatrix] = useState({});
  const [apps, setApps] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null);

  const useProxy = window.location.protocol === 'https:';
  const baseUrl = useProxy ? '/api/matrix' : `http://${window.location.hostname}:8023/api/v1/matrix`;

  const fetchMatrix = useCallback(async () => {
    try {
      const resp = await fetch(`${baseUrl}/status`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setMatrix(data.matrix || {});
      setApps(data.apps || []);
      setGeneratedAt(data.generated_at);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchMatrix();
    const interval = setInterval(fetchMatrix, 30000);
    return () => clearInterval(interval);
  }, [fetchMatrix]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await fetch(`${baseUrl}/scan`, { method: 'POST' });
      await new Promise(r => setTimeout(r, 2000));
      await fetchMatrix();
    } catch (e) {
      setError(e.message);
    } finally {
      setScanning(false);
    }
  };

  const getCell = (appName, checkType) => {
    return matrix[appName]?.[checkType] || { status: 'unknown', detail: 'No data' };
  };

  const getOverallHealth = (appName) => {
    const statuses = CHECK_TYPES.map(ct => getCell(appName, ct).status);
    if (statuses.includes('red')) return 'red';
    if (statuses.includes('yellow')) return 'yellow';
    if (statuses.every(s => s === 'green')) return 'green';
    return 'unknown';
  };

  const getSummary = () => {
    const counts = { green: 0, yellow: 0, red: 0, unknown: 0 };
    apps.forEach(app => {
      const h = getOverallHealth(app);
      counts[h] = (counts[h] || 0) + 1;
    });
    return counts;
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFC65D]"></div>
        <span className="ml-3 text-gray-600">Loading status matrix...</span>
      </div>
    );
  }

  if (error && apps.length === 0) {
    return (
      <div className="text-center py-16">
        <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
        <p className="text-red-600 font-medium">Failed to load status matrix</p>
        <p className="text-gray-500 text-sm mt-1">{error}</p>
        <button onClick={fetchMatrix} className="mt-4 px-4 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">
          Retry
        </button>
      </div>
    );
  }

  const summary = getSummary();

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Infrastructure Status</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {generatedAt ? `Updated ${timeAgo(generatedAt)}` : 'Loading...'}
            {error && <span className="text-yellow-600 ml-2">(refresh error: {error})</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {summary.green > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                {summary.green} healthy
              </span>
            )}
            {summary.yellow > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                {summary.yellow} warning
              </span>
            )}
            {summary.red > 0 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                {summary.red} critical
              </span>
            )}
          </div>
          <button
            onClick={triggerScan}
            disabled={scanning}
            className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              scanning
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#FFC65D] text-gray-900 hover:bg-[#e6b050]'
            }`}
          >
            {scanning ? (
              <><i className="fas fa-spinner fa-spin mr-1.5"></i>Scanning...</>
            ) : (
              <><i className="fas fa-sync-alt mr-1.5"></i>Scan Now</>
            )}
          </button>
        </div>
      </div>

      {/* Matrix table */}
      <div className="overflow-x-auto -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-3 border-b border-gray-200 w-40">
                App
              </th>
              {CHECK_TYPES.map(ct => (
                <th key={ct} className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-2 border-b border-gray-200">
                  <i className={`fas ${CHECK_ICONS[ct]} mr-1`}></i>
                  {CHECK_LABELS[ct]}
                </th>
              ))}
              <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-2 border-b border-gray-200 w-20">
                Overall
              </th>
            </tr>
          </thead>
          <tbody>
            {apps.map((appName, idx) => {
              const overall = getOverallHealth(appName);
              const oc = STATUS_COLORS[overall];
              return (
                <tr key={appName} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="py-3 px-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${oc.bg} flex-shrink-0`}></span>
                      <span className="font-medium text-sm text-gray-900 truncate">{appName}</span>
                    </div>
                  </td>
                  {CHECK_TYPES.map(ct => {
                    const cell = getCell(appName, ct);
                    const sc = STATUS_COLORS[cell.status] || STATUS_COLORS.unknown;
                    const cellKey = `${appName}:${ct}`;
                    const isExpanded = expandedCell === cellKey;

                    return (
                      <td key={ct} className="py-2 px-2 border-b border-gray-100 text-center relative">
                        <button
                          onClick={() => setExpandedCell(isExpanded ? null : cellKey)}
                          className={`inline-flex items-center justify-center w-full px-2 py-1.5 rounded-md text-xs font-medium transition-all ${sc.light} ${sc.text} ${sc.border} border hover:shadow-sm`}
                          title={cell.detail}
                        >
                          <span className={`w-2 h-2 rounded-full ${sc.bg} mr-1.5 flex-shrink-0`}></span>
                          <span className="truncate max-w-[120px]">
                            {cell.status === 'green' ? 'OK' :
                             cell.status === 'red' ? 'FAIL' :
                             cell.status === 'yellow' ? 'WARN' : '---'}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="absolute z-20 mt-1 left-1/2 -translate-x-1/2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-sm text-gray-900">{appName} — {CHECK_LABELS[ct]}</span>
                              <button onClick={() => setExpandedCell(null)} className="text-gray-400 hover:text-gray-600">
                                <i className="fas fa-times text-xs"></i>
                              </button>
                            </div>
                            <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.light} ${sc.text} mb-2`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.bg} mr-1`}></span>
                              {cell.status.toUpperCase()}
                            </div>
                            <p className="text-xs text-gray-600 break-words">{cell.detail}</p>
                            {cell.updated_at && (
                              <p className="text-xs text-gray-400 mt-2">Updated {timeAgo(cell.updated_at)}</p>
                            )}
                            {cell.issue_since && (
                              <p className="text-xs text-red-500 mt-1">Issue since {timeAgo(cell.issue_since)}</p>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2 px-2 border-b border-gray-100 text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${oc.light} ${oc.text}`}>
                      {overall === 'green' ? 'HEALTHY' :
                       overall === 'red' ? 'CRITICAL' :
                       overall === 'yellow' ? 'WARNING' : '---'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span className="font-medium">Legend:</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Healthy</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Warning</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Critical</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300"></span> Unknown</span>
        <span className="ml-auto">Auto-refreshes every 30s</span>
      </div>
    </div>
  );
}

export default StatusMatrix;
